import { useEffect, useMemo, useState } from 'react'
import { buildLadder } from '../ladder'
import { DepthChart } from './DepthChart'
import { DepthLadder } from './DepthLadder'
import { OrdersTab } from './OrdersTab'
import type { Depth, ShadowOrder } from '../types'

/**
 * Emir defteri paneli — sekmeli.
 *
 *   Emir Tablosu  · aynalı defter, sayfalı
 *   Emir Grafiği  · kümülatif derinlik eğrisi
 *   Emirlerim     · açık gölge emirlerimiz (düzenle / sil / geçmiş)
 *
 * Sayfa durumu burada tutuluyor: sayfalayıcı sekme şeridinin sağ ucunda
 * duruyor ve yalnız tablo sekmesinde görünüyor.
 */

interface Props {
  depth: Depth | null
  orders: ShadowOrder[]
  contract: string | null
  closed: boolean
  mcp?: number | null
  rowsPerPage?: number
}

type TabId = 'table' | 'chart' | 'orders'

const TABS: { id: TabId; label: string }[] = [
  { id: 'table', label: 'Emir Tablosu' },
  { id: 'chart', label: 'Emir Grafiği' },
  { id: 'orders', label: 'Emirlerim' },
]

export function DepthPanel({
  depth,
  orders,
  contract,
  closed,
  mcp = null,
  rowsPerPage = 10,
}: Props) {
  const [tab, setTab] = useState<TabId>('table')
  const [requestedPage, setRequestedPage] = useState(0)

  // Defter satırları burada kuruluyor, `DepthLadder`de değil: sayfa sayısı da
  // aynı listeye bağlı. Kendi emrimiz makasın içindeyse fazladan satır açılıyor,
  // sayfalayıcı onu da saymalı.
  const ladder = useMemo(() => buildLadder(depth, orders), [depth, orders])
  const totalPages = Math.max(1, Math.ceil(ladder.rowCount / rowsPerPage))

  // Kontrat değişince başa dön — yeni defterin 3. sayfasında açılmak anlamsız.
  useEffect(() => setRequestedPage(0), [contract])

  // Tahta daralıp sayfa sayısı düşerse geçerli son sayfaya çekil ve ORADA KAL.
  // Yalnızca render sırasında kırpsaydık, defter sayfa sınırında dalgalandıkça
  // (saniyede bir güncelleniyor) ekran kullanıcı hiçbir şey yapmadan sayfa
  // değiştirip dururdu.
  useEffect(() => setRequestedPage((p) => Math.min(p, totalPages - 1)), [totalPages])

  const page = Math.min(requestedPage, totalPages - 1)
  const acikEmir = orders.filter((o) => o.status === 'OPEN').length

  return (
    <div className="panel">
      <div className="panel-tabs" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={`panel-tab${tab === item.id ? ' active' : ''}`}
            onClick={() => setTab(item.id)}
          >
            {item.label}
            {/* Açık emir sayacı: başka sekmedeyken de emrimiz olduğu görünsün. */}
            {item.id === 'orders' && acikEmir > 0 && (
              <span className="tab-badge">{acikEmir}</span>
            )}
          </button>
        ))}

        {tab === 'table' && (
          <span className="ladder-pager">
            <button
              type="button"
              className="pager-btn"
              onClick={() => setRequestedPage(page - 1)}
              disabled={page === 0}
              aria-label="Önceki seviyeler"
            >
              ‹
            </button>
            <span className="pager-label">
              {page + 1}/{totalPages}
            </span>
            <button
              type="button"
              className="pager-btn"
              onClick={() => setRequestedPage(page + 1)}
              disabled={page >= totalPages - 1}
              aria-label="Sonraki seviyeler"
            >
              ›
            </button>
          </span>
        )}
      </div>

      {tab === 'table' && (
        <DepthLadder
          ladder={ladder}
          hasDepth={depth != null}
          page={page}
          rowsPerPage={rowsPerPage}
          contract={contract}
          closed={closed}
        />
      )}
      {tab === 'chart' && <DepthChart depth={depth} mcp={mcp} />}
      {tab === 'orders' && (
        <OrdersTab orders={orders} contract={contract} closed={closed} depth={depth} />
      )}
    </div>
  )
}
